import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, ChevronRight, Play, Bug } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import api from "lib/api";
import { debugImageUrl } from "lib/media";

const VERDICT_STYLE = {
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  skipped: "bg-muted text-muted-foreground border-border",
  error: "bg-red-500/15 text-red-400 border-red-500/30",
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`;
}

function highlightJson(value) {
  if (value == null) return "";
  const esc = JSON.stringify(value, null, 2)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.replace(
    /("(\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (m, str, _c, colon, lit, num) => {
      if (str !== undefined) {
        return colon
          ? `<span class="text-sky-400">${str}</span>${colon}`
          : `<span class="text-emerald-400">${str}</span>`;
      }
      if (lit !== undefined) return `<span class="text-amber-400">${lit}</span>`;
      if (num !== undefined) return `<span class="text-purple-400">${num}</span>`;
      return m;
    });
}

const pct = (p) => (p == null ? "—" : `${Math.round(p * 100)}%`);

function Row({ e }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(e.prompt || "");
  const [threshold, setThreshold] = useState(
    e.threshold != null ? Math.round(e.threshold * 100) : 70);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const params = e.request_params || {};
  const endpoint = params.endpoint;
  const fallback = params.fallback;

  const reevaluate = async () => {
    setBusy(true); setResult(null);
    try {
      const res = await api.post(`/api/analysis/debug/${e.id}/reevaluate/`,
        { prompt, threshold });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Falha ao reavaliar." });
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40"
      >
        <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
        <span className="font-mono text-xs text-muted-foreground w-24 shrink-0">{fmtTime(e.created)}</span>
        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{e.stage}</span>
        <span className="text-sm flex-1 truncate">{e.verdict_reason}</span>
        {fallback && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 shrink-0">fallback</span>
        )}
        <span className="font-mono text-xs text-muted-foreground shrink-0">{e.duration_ms ?? "—"} ms</span>
        <span className="font-mono text-xs shrink-0">IA {pct(e.ai_probability)}</span>
        <span className={`text-xs px-2 py-0.5 rounded border shrink-0 ${VERDICT_STYLE[e.verdict] || ""}`}>
          {e.verdict}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border grid gap-3 md:grid-cols-2">
          {/* Left: image sent + local output + call metadata */}
          <div className="space-y-2">
            {e.has_image && (
              <a href={debugImageUrl(e.id)} target="_blank" rel="noreferrer">
                <img src={debugImageUrl(e.id)} alt="frame enviado à IA"
                     className="rounded border border-border max-h-56" />
              </a>
            )}
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Saída do modelo local</div>
              <pre className="text-xs bg-muted/40 rounded p-2 overflow-auto"
                   dangerouslySetInnerHTML={{ __html: highlightJson(e.local_output) }} />
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">
                Parâmetros da requisição (endpoint / fallback / timeout)
              </div>
              <pre className="text-xs bg-muted/40 rounded p-2 overflow-auto"
                   dangerouslySetInnerHTML={{ __html: highlightJson(params) }} />
            </div>
            <div className="text-xs text-muted-foreground">
              Modelo IA: <span className="font-mono">{e.ai_model || "—"}</span> · endpoint{" "}
              <span className="font-mono">{endpoint || "—"}{fallback ? " (fallback)" : ""}</span> ·{" "}
              {e.duration_ms ?? "—"} ms
              {e.ai_reason ? <span> · motivo <span className="font-mono">{e.ai_reason}</span></span> : null}
              {e.error ? <span className="text-red-400"> · erro: {e.error}</span> : null}
            </div>
          </div>

          {/* Right: prompt (editable) + response + reevaluate */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">Prompt enviado (editável)</div>
            <textarea
              value={prompt} onChange={(ev) => setPrompt(ev.target.value)}
              rows={6}
              className="w-full text-xs font-mono bg-muted/40 rounded p-2 border border-border"
            />
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Limiar %</label>
              <input type="number" min={0} max={100} value={threshold}
                     onChange={(ev) => setThreshold(Number(ev.target.value))}
                     className="w-20 text-xs bg-muted/40 rounded p-1 border border-border" />
              <Button size="sm" variant="outline" onClick={reevaluate} disabled={busy}>
                <Play className="h-3.5 w-3.5 mr-1" /> Reavaliar
              </Button>
            </div>

            {result && (
              <div className="text-xs bg-muted/40 rounded p-2 border border-border">
                {result.error ? (
                  <span className="text-red-400">{result.error}</span>
                ) : (
                  <>
                    <div>Nova probabilidade: <b>{pct(result.probability)}</b> · limiar {result.threshold}% →{" "}
                      <span className={`px-1.5 py-0.5 rounded border ${VERDICT_STYLE[result.verdict] || ""}`}>
                        {result.verdict}</span> ({result.duration_ms} ms{result.fallback ? ", fallback" : ""})</div>
                    <div className="mt-1 text-muted-foreground">{result.reason}</div>
                    {result.response_raw &&
                      <pre className="mt-1 overflow-auto">{result.response_raw}</pre>}
                  </>
                )}
              </div>
            )}

            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Resposta crua da IA</div>
              <pre className="text-xs bg-muted/40 rounded p-2 overflow-auto max-h-40">{e.response_raw || "—"}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SessionDebug() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = stage ? `?stage=${stage}` : "";
      const res = await api.get(`/api/sessions/${id}/debug/${qs}`);
      setEntries(res.data.entries || []);
    } catch { setEntries([]); } finally { setLoading(false); }
  }, [id, stage]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4 max-w-6xl mx-auto w-full">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/liveness/sessions/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">Debug da análise (IA)</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select value={stage} onChange={(e) => setStage(e.target.value)}
                  className="text-sm bg-muted/40 rounded p-1.5 border border-border">
            <option value="">Todos os estágios</option>
            <option value="liveness">Vivacidade</option>
            <option value="gesture">Gesto</option>
          </select>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Cada linha é uma chamada à IA (Ollama): a saída do modelo local, a imagem e o prompt enviados,
        os parâmetros (qual endpoint respondeu, se houve fallback, timeout), a latência, a resposta crua
        e o veredito. Edite o prompt e reavalie a mesma imagem para calibrar.
      </p>

      <Card>
        <CardContent className="p-3 space-y-2">
          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Carregando…</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Sem registros de debug para esta sessão.
            </div>
          ) : (
            entries.map((e) => <Row key={e.id} e={e} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
}
